<template>
  <v-card>
    <v-card-title class="text-h5 grey lighten-4">{{ $t("search-for-parcels") }}</v-card-title>
    <v-card-text class="ma-0 pa-0" style="max-height: calc(100vh - 200px); overflow-y: auto">
      <v-form ref="form" v-model="data.valid" lazy-validation>
        <v-card class="overflow-hidden" v-if="data.loaded">
          <v-row no-gutters>
            <!-- COLUMN 1: Location & Property -->
            <v-col cols="12" md="4" class="pa-6 border-right">
              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-map-marker-radius</v-icon> {{ $t("location-context") }}</div>

              <div class="mb-4">
                <label class="caption font-weight-bold">{{ $t("canton") }}</label>
                <SelectCanton v-model="data.form.CANTON" />
              </div>

              <div class="mb-4">
                <label class="caption font-weight-bold">{{ $t("municipalities") }}</label>
                <SelectLocationName :disabled="!data.form.CANTON" dense v-model="data.form.LOCNAME" :canton="data.form.CANTON" />
              </div>

              <v-row dense>
                <v-col cols="6">
                  <label class="caption font-weight-bold">{{ $t("address") }}</label>
                  <SelectStreet :disabled="!data.form.CANTON" v-model="data.form.STREET" :canton="data.form.CANTON" :locname="data.form.LOCNAME" />
                </v-col>
                <v-col cols="6">
                  <label class="caption font-weight-bold">{{ $t("max-slope") }} (%)</label>
                  <v-select v-model="data.form.PRC_SLO" :items="['', 5, 10, 15, 20, 25, 30]" suffix="%" outlined dense clearable></v-select>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-home-variant-outline</v-icon> {{ $t("property-type") }}</div>
              <v-row dense>
                <v-col cols="4">
                  <v-card :color="data.form.OBJECT_OPTIONS === 'without' ? 'indigo lighten-3' : ''" :dark="data.form.OBJECT_OPTIONS === 'without'" @click="data.form.OBJECT_OPTIONS ? (data.form.OBJECT_OPTIONS = null) : (data.form.OBJECT_OPTIONS = 'without')" outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-square-outline</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("empty") }}</div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card :color="data.form.OBJECT_OPTIONS === 'with' ? 'indigo lighten-3' : ''" :dark="data.form.OBJECT_OPTIONS === 'with'" @click="data.form.OBJECT_OPTIONS ? (data.form.OBJECT_OPTIONS = null) : (data.form.OBJECT_OPTIONS = 'with')" outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-home-outline</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("building") }}</div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card disabled outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-scissors-cutting</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("divide") }}</div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card disabled outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-account-outline</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("owner") }}</div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card :color="data.form.PRC_TYP === 'Baurecht' ? 'indigo lighten-3' : ''" :dark="data.form.PRC_TYP === 'Baurecht'" @click="data.form.PRC_TYP = 'Baurecht'" outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-file-document-outline</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("lease") }}</div>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card :color="data.form.PRC_TYP === 'Eigentum' ? 'indigo lighten-3' : ''" :dark="data.form.PRC_TYP === 'Eigentum'" @click="data.form.PRC_TYP = 'Eigentum'" outlined elevation="1" class="pa-2 d-flex flex-column align-center justify-center">
                    <v-icon class="mb-1">mdi-tag-outline</v-icon>
                    <div class="caption font-weight-bold text-uppercase">{{ $t("for-sale") }}</div>
                  </v-card>
                </v-col>

                <v-col>
                  <!-- Building Details (Conditional) -->
                  <v-card flat class="mt-4 pa-0" v-show="data.form.OBJECT_OPTIONS == 'with'">
                    <div class="mt-4 pa-4 indigo lighten-5 rounded-lg border-indigo">
                      <div class="caption indigo--text font-weight-black mb-2 text-uppercase">{{ $t("building-details") }}</div>
                      <v-row dense>
                        <v-col><v-text-field v-model.number="data.form.OBJECTS.OBJ_JOB_NUM" :label="$t('building-details')" dense background-color="white" outlined hide-details class="mb-2"></v-text-field></v-col>
                        <v-col><v-text-field v-model.number="data.form.OBJECTS.OBJ_VOL_NUM" :label="$t('Volume') + '(m³)'" dense background-color="white" outlined hide-details class="mb-2"></v-text-field></v-col>
                      </v-row>
                      <v-row dense>
                        <v-col><v-text-field v-model.number="data.form.OBJECTS.HEIGHT" label="Height (m)" dense background-color="white" outlined hide-details class="mb-2"></v-text-field></v-col>
                        <v-col><v-text-field v-model.number="data.form.OBJECTS.OBJ_FLO_NUM" label="Floors" dense background-color="white" outlined hide-details class="mb-2"></v-text-field></v-col>
                      </v-row>
                      <div class="mb-4">
                        <v-text-field v-model.number="data.form.OBJECTS.OBJ_UNI_NUL" label="Units" dense background-color="white" outlined hide-details></v-text-field>
                      </div>
                      <div class="caption font-weight-bold mb-1">{{ $t("solar-potential") }}</div>
                      <v-btn-toggle v-if="false" v-model="data.form.OBJECTS.OBJ_SOLAR" mandatory dense color="indigo" class="d-flex">
                        <v-btn value="LOW" small class="flex-grow-1">Low</v-btn>
                        <v-btn value="MID" small class="flex-grow-1">Mid</v-btn>
                        <v-btn value="HIGH" small class="flex-grow-1">High</v-btn>
                      </v-btn-toggle>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <div class="text-subtitle-1 primary--text font-weight-bold mt-6 mb-4"><v-icon small class="mr-2">mdi-compass-outline</v-icon> {{ $t("orientation-size") }}</div>
              <v-row>
                <v-col cols="5">
                  <div class="compass-grid-vuetify">
                    <v-btn v-for="dir in data.directionsSourceMap" :key="dir.value" x-small depressed :color="data.form.PRC_ASP.includes(dir.value) ? 'indigo' : 'grey lighten-3'" :dark="data.form.PRC_ASP.includes(dir.value)" @click="toggleOrientation(dir.value)" class="compass-btn" :title="dir.value" :disabled="dir.label === 'x'">
                      {{ dir.label }}
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="7">
                  <div class="caption font-weight-bold mb-1">{{ $t("plot-size") }} (m²)</div>
                  <v-row dense>
                    <v-col><v-text-field v-model.number="data.form.PRC_SUR_from" placeholder="Min" outlined dense type="number" hide-details clearable></v-text-field></v-col>
                    <v-col><v-text-field v-model.number="data.form.PRC_SUR_to" placeholder="Max" outlined dense type="number" hide-details clearable></v-text-field></v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>

            <!-- COLUMN 2: Geology & Environment -->
            <v-col cols="12" md="4" class="pa-6 border-right">
              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-image-filter-hdr</v-icon> {{ $t("geology-env") }}</div>
              <div
                v-for="field in [
                  {label: 'Soil Contamination', model: 'GRN_CON_LABEL'},
                  {label: 'Radon Levels > 10%', model: 'GRN_RAD_LABEL'},
                  {label: 'Natural Hazards', model: 'HAZ_ANY'},
                  // {label: 'Heritage Protection (OBJ_PRO)', model: 'OBJ_PRO'},
                ]"
                :key="field.model"
                class="mb-3"
              >
                <v-row>
                  <v-col cols="7">
                    <div class="caption font-weight-bold mb-1">{{ field.label }}</div>
                  </v-col>
                  <v-col cols="5" class="d-flex justify-end">
                    <v-btn-toggle v-model="data.form[field.model]" mandatory dense color="indigo" class="d-flex">
                      <v-btn :value="null" x-small class="flex-grow-1">Any</v-btn>
                      <v-btn :value="'N'" x-small class="flex-grow-1">No</v-btn>
                      <v-btn :value="'Y'" x-small class="flex-grow-1">Yes</v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-6"></v-divider>

              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-tree-outline</v-icon> {{ $t("natural-features") }}</div>
              <v-text-field v-model.number="data.form.ENV_WAT" label="Min Distance to Water (m)" outlined dense type="number" min="0.1" hide-details class="mb-4" clearable></v-text-field>
              <v-text-field v-model.number="data.form.ENV_FOR" label="Min Distance to Forest (m)" outlined dense type="number" min="0.1" hide-details class="mb-4" clearable></v-text-field>

              <v-divider class="my-6"></v-divider>

              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-lightning-bolt-outline</v-icon> {{ $t("energy-potential") }}</div>
              <v-row dense>
                <v-col cols="6">
                  <v-card outlined class="pa-3 text-center grey lighten-5">
                    <v-icon color="brown">mdi-land-plots</v-icon>
                    <div class="caption font-weight-bold my-2 text-uppercase">{{ $t("soil-based") }}</div>
                    <v-switch v-model="data.form.GRN_TRM" hide-details class="mt-0 justify-center d-flex" color="indigo"></v-switch>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-3 text-center grey lighten-5">
                    <v-icon color="blue">mdi-water-outline</v-icon>
                    <div class="caption font-weight-bold my-2 text-uppercase">{{ $t("water-based") }}</div>
                    <v-switch v-model="data.form.ENV_GWA" hide-details class="mt-0 justify-center d-flex" color="indigo"></v-switch>
                  </v-card>
                </v-col>
              </v-row>

              <div class="mt-8">
                <div class="text-subtitle-1 primary--text font-weight-bold mb-2">{{ $t("usage-zone") }}</div>
                <div class="mb-4">
                  <label class="caption font-weight-bold">{{ $t("primary-zone") }}</label>
                  <SelectAlpha v-model="data.form.ALPHA_PRIS" :varname="'ALPHA_PRI'" :canton="data.form.CANTON" :disabled="!data.form.CANTON" />
                </div>
                <div>
                  <label class="caption font-weight-bold">{{ $t("overlay-zones") }}</label>
                  <SelectAlpha v-model="data.form.BRAVO_PRIS" :varname="'BRAVO_PRI'" :canton="data.form.CANTON" :disabled="!data.form.CANTON" />
                </div>
                <v-row dense class="mt-2" v-if="false">
                  <v-col cols="6">
                    <v-text-field clearable v-model.number="data.form.kA" label="Building Index (kA)" outlined dense type="number" step="0.1" hide-details></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field clearable v-model.number="data.form.gA" label="Floor Area (gA)" outlined dense type="number" step="0.1" hide-details></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-col>

            <!-- COLUMN 3: Distances & Pricing -->
            <v-col cols="12" md="4" class="pa-6 lighten-5">
              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-map-marker-distance</v-icon> {{ $t("max-distances") }}</div>
              <v-text-field v-model.number="data.form.AUTOBAHN_DIST_KM" :label="$t('freeway')" suffix="km" outlined dense background-color="white" type="number" step="0.1" min="0.1" prepend-inner-icon="mdi-road-variant" hide-details class="mb-2" clearable></v-text-field>
              <v-text-field v-model.number="data.form.HALTESTELLE_DIST_KM" :label="$t('public-transport')" suffix="km" outlined dense background-color="white" type="number" step="0.1" min="0.1" prepend-inner-icon="mdi-bus" hide-details class="mb-2" clearable></v-text-field>
              <v-text-field v-model.number="data.form.MARKET_DIST_KM" :label="$t('shopping')" suffix="km" outlined dense background-color="white" type="number" step="0.1" min="0.1" prepend-inner-icon="mdi-cart-outline" hide-details class="mb-2" clearable></v-text-field>
              <v-text-field v-model.number="data.form.SCHULE_DIST_KM" :label="$t('schools')" suffix="km" outlined dense background-color="white" type="number" step="0.1" min="0.1" prepend-inner-icon="mdi-school-outline" hide-details class="mb-2" clearable></v-text-field>
              <v-text-field v-model.number="data.form.HOSPITAL_DIST_KM" :label="$t('hospitals')" suffix="km" outlined dense background-color="white" type="number" step="0.1" min="0.1" prepend-inner-icon="mdi-hospital-building" hide-details class="mb-2" clearable></v-text-field>

              <v-divider class="my-6"></v-divider>

              <div class="text-subtitle-1 primary--text font-weight-bold mb-4"><v-icon small class="mr-2">mdi-cash-multiple</v-icon> {{ $t("pricing") }}</div>
              <v-card outlined class="pa-4 mb-4" :disabled="true">
                <div class="caption font-weight-bold mb-2">{{ $t("price-per") }} m²</div>
                <v-row dense>
                  <v-col><v-text-field v-model.number="data.form.pricePerM2Min" placeholder="Min" dense hide-details outlined type="number"></v-text-field></v-col>
                  <v-col><v-text-field v-model.number="data.form.pricePerM2Max" placeholder="Max" dense hide-details outlined type="number"></v-text-field></v-col>
                </v-row>
              </v-card>
              <v-card outlined class="pa-4" :disabled="true">
                <div class="caption font-weight-bold mb-2">{{ $t("total-price") }}</div>
                <v-row dense>
                  <v-col><v-text-field v-model.number="data.form.totalPriceMin" placeholder="Min" dense hide-details outlined type="number"></v-text-field></v-col>
                  <v-col><v-text-field v-model.number="data.form.totalPriceMax" placeholder="Max" dense hide-details outlined type="number"></v-text-field></v-col>
                </v-row>
              </v-card>

              <v-divider class="my-6"></v-divider>

              <div class="text-subtitle-1 primary--text font-weight-bold mb-4 text-center"><v-icon small class="mr-2">mdi-star-outline</v-icon> {{ $t("plot-rating") }}</div>
              <div class="d-flex justify-center">
                <!-- Vuetify 2 v-rating uses mdi-star by default -->
                <v-rating readonly v-model="data.form.plotRatingScore" color="yellow darken-3" background-color="grey lighten-2" hover large></v-rating>
              </div>
            </v-col>
          </v-row>
        </v-card>
        <v-card v-else flat class="d-flex align-center justify-center" style="height: 300px">
          <v-progress-circular indeterminate color="indigo"></v-progress-circular>
          <div class="ml-4 text-subtitle-2 grey--text">{{ $t("loading") }}</div>
        </v-card>
      </v-form>
    </v-card-text>
    <!-- Footer Actions -->
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn color="primary" @click="advanceSearch" :disabled="data.searching">
        <span v-if="data.searching">{{ $t("loading") }}</span>
        <span v-else>{{ $t("search") }}</span>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn text color="grey darken-1" @click="resetForm" class="font-weight-bold"> {{ $t("reset-all-filters") }} </v-btn>
      <v-btn text color="grey darken-1" @click="$emit('closeAdvanceSearchDialog')" class="font-weight-bold"> {{ $t("close") }} </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {Vue, Component, Prop, Watch} from "vue-property-decorator"
import SelectCanton from "./../component/canton/Select.vue"
import SelectLocationName from "./../component/location-names/Select.vue"
import SelectAlpha from "./../component/alpha/Select.vue"
import SelectStreet from "./../component/street/Select.vue"

@Component({
  components: {
    SelectCanton,
    SelectLocationName,
    SelectStreet,
    SelectAlpha,
  },
})
export default class AdvanceSearch extends Vue {
  data = {
    valid: true,
    searchDialog: false,
    searchObject: {},
    searching: false,
    loaded: false,
    form: {
      CANTON: null,
      LOCNAME: [],
      STREET: [],
      PRC_SLO: null,
      PRC_TYP: null,
      PRC_ASP: [],
      PRC_SUR_from: null,
      PRC_SUR_to: null,
      YROFBUILD: null,
      OBJ_VOL: null,
      HEIGHT: null,
      OBJ_FLO: null,
      OBJ_UNI: null,
      OBJ_SOLAR: null,
      GRN_CON_LABEL: false,
      GRN_RAD: false,
      HAZ_ANY: false,
      OBJ_PRO: false,
      ENV_WAT: null,
      ENV_FOR: null,
      GRN_TRM: false,
      ENV_GWA: false,
      ALPHA_PRI: [],
      BRAVO_PRI: [],
      kA: null,
      gA: null,
      AUTOBAHN_DIST_KM: null,
      HALTESTELLE_DIST_KM: null,
      MARKET_DIST_KM: null,
      SCHULE_DIST_KM: null,
      HOSPITAL_DIST_KM: null,
      pricePerM2Min: null,
      pricePerM2Max: null,
      totalPriceMin: null,
      totalPriceMax: null,
      plotRatingScore: null,
      OBJECT_OPTIONS: null,
      OBJECTS: {},
    },
    directions: ["NW", "N", "NE", "W", "x", "E", "SW", "S", "SE"],
    directionsSourceMap: [
      {label: "NW", value: "Nordwest"},
      {label: "N", value: "Nord"},
      {label: "NE", value: "Nordost"},
      {label: "W", value: "West"},
      {label: "x", value: ""},
      {label: "E", value: "Ost"},
      {label: "SW", value: "Südwest"},
      {label: "S", value: "Süd"},
      {label: "SE", value: "Südost"},
    ],
    propertyTypes: [
      {label: "Empty", model: "PRC_TYP", icon: "mdi-square-outline", value: "Empty"},
      {label: "Building", model: "PRC_TYP", icon: "mdi-home-outline", value: "Building"},
      {label: "Divide", model: "PRC_TYP", icon: "mdi-scissors-cutting", disabled: true, value: "Divide"},
      {label: "Owner", model: "PRC_TYP", icon: "mdi-account-outline", disabled: true, value: "Owner"},
      {label: "Lease", model: "PRC_TYP", icon: "mdi-file-document-outline", value: "Baurecht"},
      {label: "For Sale", model: "PRC_TYP", icon: "mdi-tag-outline", value: "Eigentum"},
    ],
  }

  @Prop({required: true}) searchDialog

  @Prop({required: true}) searchObject

  mounted() {
    if (this.searchObject) {
      const cloned = JSON.parse(JSON.stringify(this.searchObject))
      this.data.searchObject = cloned
      this.data.form = {...this.data.form, ...cloned}
      this.data.loaded = true
    } else {
      this.data.loaded = true
    }
  }

  @Watch("data.form.CANTON")
  onCantonSet(val) {
    this.data.form.LOCNAME = []
    this.data.form.STREET = []
    this.data.form.ALPHA_PRI = []
    this.data.form.BRAVO_PRI = []
  }

  toggleOrientation(dir) {
    const index = this.data.form.PRC_ASP.indexOf(dir)
    if (index > -1) this.data.form.PRC_ASP.splice(index, 1)
    else this.data.form.PRC_ASP.push(dir)
  }

  resetForm() {
    this.data.form = {
      CANTON: null,
      LOCNAME: [],
      STREET: [],
      PRC_SLO: null,
      PRC_TYP: null,
      PRC_ASP: [],
      PRC_SUR_from: null,
      PRC_SUR_to: null,
      YROFBUILD: null,
      OBJ_VOL: null,
      HEIGHT: null,
      OBJ_FLO: null,
      OBJ_UNI: null,
      OBJ_SOLAR: null,
      GRN_CON_LABEL: false,
      GRN_RAD: false,
      HAZ_ANY: false,
      OBJ_PRO: false,
      ENV_WAT: null,
      ENV_FOR: null,
      GRN_TRM: false,
      ENV_GWA: false,
      ALPHA_PRI: [],
      BRAVO_PRI: [],
      kA: null,
      gA: null,
      AUTOBAHN_DIST_KM: null,
      HALTESTELLE_DIST_KM: null,
      MARKET_DIST_KM: null,
      SCHULE_DIST_KM: null,
      HOSPITAL_DIST_KM: null,
      pricePerM2Min: null,
      pricePerM2Max: null,
      totalPriceMin: null,
      totalPriceMax: null,
      plotRatingScore: null,
      OBJECT_OPTIONS: null,
      OBJECTS: {},
    }
  }

  advanceSearch() {
    // Clone form data to build search object
    let searchObject = JSON.parse(JSON.stringify(this.data.form))

    // Handle Boolean flags correctly for backend (might need specific values like "Standard" or true/false)
    // Map.vue uses GRN_RAD as a boolean that composeQuery turns into "0-9"

    // PRC_IND is missing in AdvanceSearch but used in Map.vue
    searchObject.PRC_IND = "Standard"

    //spread "OBJECTS" properties to use . notation for prop OBJECTS.PROPNAME
    console.log("searchObject.OBJECTS", searchObject.OBJECTS)
    if (searchObject.OBJECTS) {
      for (const [key, value] of Object.entries(searchObject.OBJECTS)) {
        searchObject["OBJECTS." + key] = value
      }
      delete searchObject.OBJECTS
    }
    console.log("searchObject", searchObject)

    // Emit search event to parent (Map.vue)
    this.$emit("onSearch", searchObject)
  }
}
</script>

<style>
.border-right {
  border-right: 1px solid #e0e0e0;
}

.border-indigo {
  border: 1px solid #3f51b5 !important;
}

.compass-grid-vuetify {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 120px;
}

.compass-btn {
  min-width: 36px !important;
  padding: 0 !important;
  font-size: 10px !important;
}

.w-full {
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.uppercase {
  text-transform: uppercase;
}
</style>
